/**
 * React hook for emergency participant management with adaptive retry
 */

import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  GET_EMERGENCY_PARTICIPANT_COUNT,
  REGISTER_EMERGENCY_PARTICIPANT,
  UNREGISTER_EMERGENCY_PARTICIPANT,
} from '../graphql/emergencyQueries';

export interface UseEmergencyParticipantResult {
  participantCount: number;
  loading: boolean;
  error: Error | null;
  registerParticipant: (emergencyId: string, userId: string) => Promise<number>;
  unregisterParticipant: (emergencyId: string, userId: string) => Promise<number>;
  refetchCount: () => void;
}

/**
 * Hook for managing emergency participant registration with adaptive retry
 * 
 * @param emergencyId - Unique emergency identifier
 * @returns Participant management functions and state
 */
export function useEmergencyParticipant(
  emergencyId: string
): UseEmergencyParticipantResult {
  const [error, setError] = useState<Error | null>(null);

  // Query participant count
  const {
    data,
    loading,
    error: queryError,
    refetch,
  } = useQuery<Record<string, any>>(GET_EMERGENCY_PARTICIPANT_COUNT, {
    variables: { emergencyId },
    pollInterval: 5000, // Refresh every 5 seconds
    fetchPolicy: 'network-only',
  });

  // Register participant mutation
  const [registerMutation, { loading: registerLoading }] = useMutation<Record<string, any>>(
    REGISTER_EMERGENCY_PARTICIPANT
  );

  // Unregister participant mutation
  const [unregisterMutation, { loading: unregisterLoading }] = useMutation<Record<string, any>>(
    UNREGISTER_EMERGENCY_PARTICIPANT
  );

  const participantCount = data?.emergencyParticipantCount || 0;

  // Register participant with error handling
  const registerParticipant = useCallback(
    async (emergencyId: string, userId: string): Promise<number> => {
      try {
        setError(null);
        const result = await registerMutation({
          variables: { emergencyId, userId },
        });
        
        const newCount = result.data?.registerEmergencyParticipant || 0;
        
        // Refetch to ensure consistency
        await refetch();
        
        return newCount;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Registration failed');
        setError(error);
        throw error;
      }
    },
    [registerMutation, refetch]
  );

  // Unregister participant with error handling
  const unregisterParticipant = useCallback(
    async (emergencyId: string, userId: string): Promise<number> => {
      try {
        setError(null);
        const result = await unregisterMutation({
          variables: { emergencyId, userId },
        });
        
        const newCount = result.data?.unregisterEmergencyParticipant || 0;
        
        // Refetch to ensure consistency
        await refetch();
        
        return newCount;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Unregistration failed');
        setError(error);
        throw error;
      }
    },
    [unregisterMutation, refetch]
  );

  // Update error state from query
  useEffect(() => {
    if (queryError) {
      setError(queryError instanceof Error ? queryError : new Error('Query failed'));
    }
  }, [queryError]);

  return {
    participantCount,
    loading: loading || registerLoading || unregisterLoading,
    error: error || (queryError as Error | null),
    registerParticipant,
    unregisterParticipant,
    refetchCount: () => refetch(),
  };
}

/**
 * Hook for getting emergency level based on participant count
 */
export function useEmergencyLevel(participantCount: number): 'P1' | 'P2' | 'P3' | 'P4' {
  if (participantCount > 50000) {
    return 'P1'; // Massive scale
  } else if (participantCount > 10000) {
    return 'P2'; // Large scale
  } else if (participantCount > 1000) {
    return 'P3'; // Medium scale
  } else {
    return 'P4'; // Small scale
  }
}

/**
 * Hook for getting recommended retry count based on participant scale
 */
export function useRecommendedRetryCount(participantCount: number): number {
  if (participantCount > 50000) {
    return 15; // High-scale emergency
  } else if (participantCount > 10000) {
    return 10; // Medium-scale emergency
  } else if (participantCount > 1000) {
    return 7; // Small-scale emergency
  } else {
    return 3; // Standard
  }
}

